// Prompt Picker Web Component

class PromptPicker extends HTMLElement {
  constructor() {
    super();
    this.prompts = [];
    this.currentPrompt = null;
    this.isLoading = false;
    this.currentUser = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();

    // Try to load prompts, with retry if API isn't ready yet
    this.loadPrompts();

    this.handleOpenProposePrompt = (e) => {
      this.openPromptModal(e.detail || {});
    };
    document.addEventListener('open-propose-prompt', this.handleOpenProposePrompt);
  }

  render() {
    this.innerHTML = `
      <div class="prompt-picker">
        <div class="prompt-display">
          <post-type-selector selected-type="text" id="promptPickerPostTypeSelector"></post-type-selector>
          <div class="prompt-actions">
            <washing-machine-icon 
              id="washingMachineBtn" 
              size="medium" 
              interactive="true"
              title="Get a writing prompt!"
            ></washing-machine-icon>
            <button class="propose-prompt-btn" id="proposePromptBtn" title="Propose a new writing prompt">
              💡
            </button>
          </div>
        </div>
        <div class="prompt-body" id="promptBody" style="display: none;"></div>
      </div>
    `;
  }

  attachEventListeners() {
    const washingMachineBtn = this.querySelector('#washingMachineBtn');
    const proposePromptBtn = this.querySelector('#proposePromptBtn');
    const postTypeSelector = this.querySelector('#promptPickerPostTypeSelector');

    // Listen for post type changes from selector
    if (postTypeSelector) {
      postTypeSelector.addEventListener('post-type-selected', (e) => {
        // Dispatch event to parent post-writer to switch types
        this.dispatchEvent(new CustomEvent('switch-post-type', {
          detail: { type: e.detail.type },
          bubbles: true
        }));
      });
    }

    washingMachineBtn.addEventListener('click', () => {
      this.getRandomPrompt();
    });

    proposePromptBtn.addEventListener('click', () => {
      this.openPromptModal();
    });
  }

  async loadPrompts() {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      // TODO: this should probably be done on the server side
      const response = await window.API.request('/prompts/public');
      this.prompts = response.prompts || [];
    } catch (error) {
      window.logger.error('Failed to load prompts:', error);
      this.prompts = [];
    } finally {
      this.isLoading = false;
    }
  }

  getRandomPrompt() {
    if (this.isLoading || this.prompts.length === 0) {
      // If no prompts loaded, try to load them
      if (this.prompts.length === 0) {
        this.loadPrompts().then(() => {
          if (this.prompts.length > 0) {
            this.getRandomPrompt();
          }
        });
      }
      return;
    }

    // Add spinning animation
    const washingMachineBtn = this.querySelector('#washingMachineBtn');
    const promptBody = this.querySelector('#promptBody');

    washingMachineBtn.spin();

    // Get a random prompt (different from current if possible)
    let newPrompt;
    if (this.prompts.length === 1) {
      newPrompt = this.prompts[0];
    } else {
      do {
        newPrompt = this.prompts[Math.floor(Math.random() * this.prompts.length)];
      } while (newPrompt === this.currentPrompt && this.prompts.length > 1);
    }

    // Update after animation
    setTimeout(() => {
      this.currentPrompt = newPrompt;

      // Don't show body here - it will be populated in the textarea or poll editor
      promptBody.style.display = 'none';

      // Dispatch event for parent components
      this.dispatchEvent(new CustomEvent('prompt-selected', {
        detail: { prompt: newPrompt },
        bubbles: true
      }));
    }, 600);
  }

  getCurrentPrompt() {
    return this.currentPrompt;
  }

  clearPrompt() {
    this.currentPrompt = null;
    const promptBody = this.querySelector('#promptBody');

    promptBody.style.display = 'none';

    // Dispatch event
    this.dispatchEvent(new CustomEvent('prompt-cleared', {
      bubbles: true
    }));
  }

  openPromptModal(opts = {}) {
    // Create true modal outside component hierarchy
    const modal = document.createElement('div');
    modal.className = 'user-prompt-modal';
    modal.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-content">
          <div class="modal-header">
            <h3>💡 Propose a Writing Prompt</h3>
            <button class="close-btn" id="closeUserModal">&times;</button>
          </div>
          <form class="user-prompt-form" id="userPromptForm">
            <div class="form-group">
              <label>Prompt Type *</label>
              <div class="prompt-type-selector">
                <label class="prompt-type-option">
                  <input type="radio" name="prompt_type" value="text" checked>
                  <span>📝 Text Prompt</span>
                </label>
                <label class="prompt-type-option">
                  <input type="radio" name="prompt_type" value="poll">
                  <span>📊 Poll Prompt</span>
                </label>
              </div>
            </div>
            <div class="form-group">
              <label for="userPromptTitle">Prompt Title *</label>
              <input 
                type="text" 
                id="userPromptTitle" 
                name="title" 
                placeholder="e.g., What made you smile today?"
                maxlength="255"
                required
              />
            </div>
            <div class="form-group" id="textPromptBodyGroup">
              <label for="userPromptBody">
                Optional Body
                <span class="help-text">(Additional context or template for the prompt)</span>
              </label>
              <textarea 
                id="userPromptBody" 
                name="body" 
                placeholder="e.g., You could do one of those lists where people bold all the things they've done..."
                rows="3"
                maxlength="1000"
              ></textarea>
            </div>
            <div class="form-group" id="pollPromptOptionsGroup" style="display: none;">
              <label for="pollPromptOptions">
                Poll Options *
                <span class="help-text">(2-10 options, one per line)</span>
              </label>
              <textarea 
                id="pollPromptOptions" 
                name="poll_options" 
                placeholder="Spring&#10;Summer&#10;Fall&#10;Winter"
                rows="6"
              ></textarea>
              <div class="option-count">
                <span id="pollOptionCount">0</span> options
              </div>
            </div>
            <div class="form-notice">
              <div class="notice-icon">🦝</div>
              <div class="notice-text">
                Your prompt will be submitted for admin review before appearing in the global pool.
                You can use it immediately for your own posts!
              </div>
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="cancelUserPrompt">Cancel</button>
              <button type="submit" class="btn btn-primary" id="submitUserPrompt">
                <span class="btn-text">Propose Prompt</span>
                <span class="btn-loading" style="display: none;">⏳ Creating...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Add event listeners
    modal.querySelector('#closeUserModal').addEventListener('click', () => {
      this.closePromptModal(modal);
    });

    modal.querySelector('#cancelUserPrompt').addEventListener('click', () => {
      this.closePromptModal(modal);
    });

    modal.querySelector('.modal-backdrop').addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-backdrop')) {
        this.closePromptModal(modal);
      }
    });

    const applyPromptTypeUI = (isPoll) => {
      const textBodyGroup = modal.querySelector('#textPromptBodyGroup');
      const pollOptionsGroup = modal.querySelector('#pollPromptOptionsGroup');
      const pollOptionsInput = modal.querySelector('#pollPromptOptions');
      if (isPoll) {
        textBodyGroup.style.display = 'none';
        pollOptionsGroup.style.display = 'block';
        pollOptionsInput.focus();
      } else {
        textBodyGroup.style.display = 'block';
        pollOptionsGroup.style.display = 'none';
      }
    };

    // Handle prompt type change
    modal.querySelectorAll('input[name="prompt_type"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        applyPromptTypeUI(e.target.value === 'poll');
      });
    });

    if (opts.presetType === 'poll') {
      const pollRadio = modal.querySelector('input[name="prompt_type"][value="poll"]');
      if (pollRadio) {
        pollRadio.checked = true;
        applyPromptTypeUI(true);
      }
    }

    // Handle poll options input
    const pollOptionsInput = modal.querySelector('#pollPromptOptions');
    if (pollOptionsInput) {
      pollOptionsInput.addEventListener('input', (e) => {
        const options = e.target.value.split('\n').filter(opt => opt.trim().length > 0);
        const countSpan = modal.querySelector('#pollOptionCount');
        if (countSpan) {
          countSpan.textContent = options.length;
        }
      });
    }

    modal.querySelector('#userPromptForm').addEventListener('submit', (e) => {
      e.preventDefault();
      this.handlePromptSubmission(modal);
    });

    // Close modal with Escape key
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        this.closePromptModal(modal);
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    // Focus on title input (or poll options when preset to poll)
    setTimeout(() => {
      if (opts.presetType === 'poll') {
        const pollOptionsInput = modal.querySelector('#pollPromptOptions');
        if (pollOptionsInput) pollOptionsInput.focus();
      } else {
        modal.querySelector('#userPromptTitle').focus();
      }
    }, 100);
  }

  closePromptModal(modal) {
    modal.remove();
  }

  async handlePromptSubmission(modal) {
    const titleInput = modal.querySelector('#userPromptTitle');
    const bodyInput = modal.querySelector('#userPromptBody');
    const pollOptionsInput = modal.querySelector('#pollPromptOptions');
    const promptTypeRadios = modal.querySelectorAll('input[name="prompt_type"]');
    const submitBtn = modal.querySelector('#submitUserPrompt');
    const btnText = submitBtn.querySelector('.btn-text');
    const btnLoading = submitBtn.querySelector('.btn-loading');

    const title = titleInput.value.trim();
    const selectedType = Array.from(promptTypeRadios).find(r => r.checked)?.value || 'text';
    const isPoll = selectedType === 'poll';

    if (!title) {
      alert('Please enter a prompt title');
      titleInput.focus();
      return;
    }

    // Validate poll options if it's a poll prompt
    if (isPoll) {
      const pollOptions = pollOptionsInput.value.split('\n')
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);

      if (pollOptions.length < 2) {
        alert('Poll prompts must have at least 2 options');
        pollOptionsInput.focus();
        return;
      }

      if (pollOptions.length > 10) {
        alert('Poll prompts can have at most 10 options');
        pollOptionsInput.focus();
        return;
      }

      // Check for duplicates (case-insensitive)
      const optionTexts = pollOptions.map(opt => opt.toLowerCase());
      const uniqueOptions = new Set(optionTexts);
      if (optionTexts.length !== uniqueOptions.size) {
        alert('Poll options must be unique');
        pollOptionsInput.focus();
        return;
      }

      // Check option length
      for (const option of pollOptions) {
        if (option.length > 500) {
          alert('Each poll option must be 500 characters or less');
          pollOptionsInput.focus();
          return;
        }
      }
    }

    // Set loading state
    submitBtn.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline';

    try {
      const promptData = {
        title: title,
        prompt_type: selectedType
      };

      if (isPoll) {
        const pollOptions = pollOptionsInput.value.split('\n')
          .map(opt => opt.trim())
          .filter(opt => opt.length > 0);
        promptData.poll_options = pollOptions;
      } else {
        const body = bodyInput.value.trim();
        promptData.body = body || null;
      }

      const response = await window.API.prompts.create(promptData);

      // Auto-select the newly created prompt
      this.currentPrompt = {
        id: response.prompt.id,
        title: response.prompt.title,
        body: response.prompt.body,
        prompt_type: response.prompt.prompt_type,
        poll_options: response.prompt.poll_options
      };

      // Update UI to show selected prompt
      const promptBody = this.querySelector('#promptBody');

      // Don't show body here - it will be populated in the textarea or poll editor
      promptBody.style.display = 'none';

      // Dispatch event for parent components
      this.dispatchEvent(new CustomEvent('prompt-selected', {
        detail: { prompt: this.currentPrompt },
        bubbles: true
      }));

      // Show success message
      this.showSuccessMessage('Prompt created and selected! Start writing with your new prompt.');

      // Close modal
      this.closePromptModal(modal);

      // Reload prompts for future use
      await this.loadPrompts();

    } catch (error) {
      window.logger.error('Failed to create prompt:', error);
      let errorMessage = 'Failed to create prompt. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details && Array.isArray(error.details)) {
        errorMessage = error.details.map(d => d.message).join(', ');
      }
      alert(errorMessage);

      // Reset button state
      submitBtn.disabled = false;
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
    }
  }

  showSuccessMessage(message) {
    // Create temporary success message
    const successEl = document.createElement('div');
    successEl.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      background: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      z-index: 10001;
      animation: slideInRight 0.3s ease-out;
      max-width: 300px;
    `;
    successEl.textContent = message;

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(successEl);

    // Remove after 4 seconds
    setTimeout(() => {
      successEl.remove();
      style.remove();
    }, 4000);
  }

  isAuthenticated() {
    return this.currentUser !== null;
  }

  disconnectedCallback() {
    if (this.handleOpenProposePrompt) {
      document.removeEventListener('open-propose-prompt', this.handleOpenProposePrompt);
    }
    // Remove event listeners when component is removed
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    if (this.handleAuthSuccess) {
      document.removeEventListener('auth-success', this.handleAuthSuccess);
    }
  }
}

// Register the custom element
customElements.define('prompt-picker', PromptPicker);
